import { Button, ConfirmDialog, DataTable, PageHead } from "@nabble/ui";
import { useMemo, useState } from "react";
import type { OrganizationMember } from "../../../api/organizations";
import { useManagedOrganization } from "../../../hooks/useManagedOrganization";
import { AddPersonDialog } from "./AddPersonDialog";
import { EditMemberRolesDialog } from "./EditMemberRolesDialog";
import { memberColumns } from "./memberColumns";
import { useOrganizationPeople } from "./useOrganizationPeople";

/** The people of an organization, managed by its administrators. */
export default function OrganizationPeoplePage(): React.JSX.Element {
  const { organizationId, picker } = useManagedOrganization();
  const { organization, roles, loading, loadError, reload, saveRoles, remove } = useOrganizationPeople(organizationId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<OrganizationMember | null>(null);
  const [removing, setRemoving] = useState<OrganizationMember | null>(null);

  const columns = useMemo(() => memberColumns({ editRoles: setEditing, remove: setRemoving }), []);

  async function confirmRemove(member: OrganizationMember): Promise<void> {
    if (await remove(member)) setRemoving(null);
  }

  return (
    <div className="nb-stack">
      <PageHead
        titleHidden
        title="People"
        actions={organizationId ? <Button label="Add person" icon="userPlus" onClick={() => setAdding(true)} /> : undefined}
      />
      {picker}
      <DataTable<OrganizationMember>
        id="organization-people"
        rowKey="id"
        loading={loading && !organization}
        rows={organization?.members ?? []}
        columns={columns}
        exportName="people"
        searchPlaceholder="Search by name or email"
        defaultSort={{ key: "displayName", dir: "asc" }}
        error={
          loadError
            ? { title: "We couldn't load the list", description: loadError, action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} /> }
            : null
        }
        empty={{
          icon: "users",
          title: "Nobody here yet",
          description: "Add the people who work in this organization.",
          action: <Button label="Add person" icon="userPlus" onClick={() => setAdding(true)} />,
        }}
      />

      {adding && organization ? (
        <AddPersonDialog
          organization={organization}
          roles={roles}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}

      {editing ? (
        <EditMemberRolesDialog
          member={editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSave={async (roleIds) => {
            if (await saveRoles(editing, roleIds)) setEditing(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        icon="userX"
        title="Remove from the organization"
        description={
          removing && organization
            ? `${removing.displayName} will stop seeing the work of ${organization.name}. Their account and their other organizations stay as they are.`
            : undefined
        }
        confirmLabel="Remove"
        onConfirm={() => (removing ? confirmRemove(removing) : undefined)}
        onClose={() => setRemoving(null)}
      />
    </div>
  );
}
